/* eslint-disable react/require-default-props, no-unused-vars */
// An individual notification
//
// Wrapper around SUI Message. Not dismissable by default.
//
import cn from 'classnames'
import React from 'react'
import Message from 'semantic-ui-react/dist/es/collections/Message'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { compose } from 'redux'

import { last } from 'util/arrays'
import withOpenState from 'shared/ui/withOpenState'
import { Icon } from 'util/ui'

import withAutoDismiss from './withAutoDismiss'
import withSlideTransition from './withSlideTransition'
import AppendEllipsis from './AppendEllipsis'
import IconBox from './IconBox'
import TextBox from './TextBox'

function getIconName(type = 'positive') {
  if (type === 'positive') return 'success-outline'
  return 'alert-outline'
}

function MessageRoot({ transitionNodeRef, ...rest }) {
  return <div {...rest} ref={transitionNodeRef} />
}

MessageRoot.propTypes = {
  transitionNodeRef: PropTypes.shape({ current: PropTypes.object }),
}

const Notification = styled(
  ({
    className,
    fluid = false,
    header = '',
    message,
    messageEllipsis = false,
    onDismiss,
    transitionNodeRef,
    type = 'positive',
  }) => {
    let headerText = header
    let messageText = message

    if (!headerText) {
      headerText = message
      messageText = null
    }

    const messageHeader = headerText ? (
      <div className="textHeader">{headerText}</div>
    ) : null

    if (messageText) {
      const pieces = messageText.split('\n')
      const lastPiece = last(pieces)

      messageText = pieces.map(piece => {
        if (!messageEllipsis || piece !== lastPiece) return <div>{piece}</div>
        return <AppendEllipsis>{piece}</AppendEllipsis>
      })
    }

    return (
      <Message
        as={MessageRoot}
        className={cn(className, { fluid })}
        icon
        {...{ [type]: true }}
        onDismiss={onDismiss}
        transitionNodeRef={transitionNodeRef}
      >
        <IconBox className="grui flex-col">
          <Icon className={`Icon Icon-${getIconName(type)}`} size="big" />
        </IconBox>
        <TextBox className="grui flex-col">
          {messageHeader}
          <Message.Content>{messageText}</Message.Content>
        </TextBox>
      </Message>
    )
  }
)`
  &&& {
    &.ui.message {
      background-color: white;
      box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.15);
      max-width: 300px;

      &.positive {
        border-left: 40px solid ${props => props.theme.color.primary.positive};
      }

      &.warning {
        border-left: 40px solid ${props => props.theme.color.primary.warning};
      }

      &.negative {
        border-left: 40px solid ${props => props.theme.color.primary.negative};
      }

      &.fluid {
        max-width: none;
      }
    }
  }
`

Notification.propTypes = {
  // Disable transition on mount
  appear: PropTypes.bool,

  // Disable limit on width
  fluid: PropTypes.bool,

  // Text content
  messageEllipsis: PropTypes.bool,

  // Text content
  header: PropTypes.string,

  // Text content
  message: PropTypes.string.isRequired,

  // Called when notification is manually closed
  onDismiss: PropTypes.func,

  // Type of notification
  type: PropTypes.oneOf(['positive', 'warning', 'negative']),
}

export default compose(
  component => withOpenState(component, { defaultInitiallyOpen: true }),
  withAutoDismiss,
  withSlideTransition
)(Notification)
